/**
 * Card Product Inline Variants
 * Handles variant selection and add to cart directly on product cards
 */

if (!customElements.get('card-inline-variants')) {
  customElements.define(
    'card-inline-variants',
    class CardInlineVariants extends HTMLElement {
      constructor() {
        super();
        this.productData = null;
        this.selectedOptions = [];
        this.currentVariant = null;
      }

      connectedCallback() {
        this.parseProductData();
        this.initializeSelectedOptions();
        this.bindEvents();
        this.updateSelectedVariant();
        this.formatPrice();
        this.buttonMode = this.hasAttribute('data-button-mode');
        
        // Auto-select first sale variant if on sale collection
        this.autoSelectSaleVariant();
      }

      autoSelectSaleVariant() {
        // Check if we're on a sale collection page
        const isSaleCollection = window.location.pathname.toLowerCase().includes('/collections/sale');
        if (!isSaleCollection || !this.productData) return;

        // Find the first variant that is on sale (compare_at_price > price) and available
        const saleVariant = this.productData.variants.find((variant) => {
          return variant.available && variant.compare_at_price && variant.compare_at_price > variant.price;
        });

        if (!saleVariant) return;

        // Get the option1 value (usually Color) from the sale variant
        const saleOption1 = saleVariant.option1;
        if (!saleOption1) return;

        // Find the input for this option value and select it
        const input = this.querySelector(`.card-inline-variants__input[data-option-value="${saleOption1}"]`);
        if (input && !input.checked) {
          input.checked = true;
          
          // Update selected options
          const optionIndex = parseInt(input.dataset.optionIndex, 10);
          this.selectedOptions[optionIndex] = saleOption1;
          
          // Update variant and UI
          this.updateSelectedVariant();
          this.updateProductImage();
          this.updateAvailability();
          this.updateSelectedName();
        }
      }

      formatPrice() {
        // Find the price element in the card
        const cardWrapper = this.closest('.card-wrapper, featured-collection-product');
        if (!cardWrapper) return;
        
        const priceItems = cardWrapper.querySelectorAll('.price-item');
        priceItems.forEach(priceItem => {
          const text = priceItem.textContent.trim();
          // Match price patterns like $25.00 and replace .00 with nothing
          const formatted = text.replace(/(\$[\d,]+)\.00\b/g, '$1');
          if (formatted !== text) {
            priceItem.textContent = formatted;
          }
        });
      }

      parseProductData() {
        const dataElement = this.querySelector('.card-inline-variants__product-data');
        if (dataElement) {
          try {
            this.productData = JSON.parse(dataElement.textContent);
          } catch (e) {
            console.error('Error parsing product data:', e);
          }
        }
      }

      initializeSelectedOptions() {
        // Get all checked inputs and build initial selected options
        const checkedInputs = this.querySelectorAll('.card-inline-variants__input:checked');
        this.selectedOptions = [];
        
        checkedInputs.forEach((input) => {
          const optionIndex = parseInt(input.dataset.optionIndex, 10);
          this.selectedOptions[optionIndex] = input.dataset.optionValue;
        });
      }

      bindEvents() {
        // Listen for option changes
        const inputs = this.querySelectorAll('.card-inline-variants__input');
        inputs.forEach((input) => {
          input.addEventListener('change', this.onOptionChange.bind(this));
        });
        
        // Listen for clicks on hover inputs (to handle re-clicking already selected option)
        const hoverInputs = this.querySelectorAll('.card-inline-variants__input--hover');
        hoverInputs.forEach((input) => {
          input.addEventListener('click', this.onHoverOptionClick.bind(this));
        });

        // Listen for "more" button clicks
        const moreBtns = this.querySelectorAll('.card-inline-variants__more-btn');
        moreBtns.forEach((btn) => {
          btn.addEventListener('click', this.onMoreClick.bind(this));
        });

        // Prevent click propagation on the entire component
        this.addEventListener('click', (e) => {
          e.stopPropagation();
        });

        this.bindHoverOptionsDesktopScroll();
      }

      /**
       * Desktop: vertical wheel does not scroll horizontal overflow by default.
       * Map wheel to scrollLeft and enable click-drag to scroll (touch already pans).
       */
      bindHoverOptionsDesktopScroll() {
        this.querySelectorAll('.card-inline-variants__hover-options').forEach((el) => {
          if (el.dataset.desktopScrollBound === 'true') return;
          el.dataset.desktopScrollBound = 'true';

          el.addEventListener(
            'wheel',
            (e) => {
              if (el.scrollWidth <= el.clientWidth) return;
              if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
              e.preventDefault();
              el.scrollLeft += e.deltaY;
            },
            { passive: false }
          );

          let drag = null;

          el.addEventListener('pointerdown', (e) => {
            if (e.pointerType === 'touch') return;
            if (el.scrollWidth <= el.clientWidth) return;
            drag = {
              pointerId: e.pointerId,
              startX: e.clientX,
              startScroll: el.scrollLeft,
              moved: false,
            };
            try {
              el.setPointerCapture(e.pointerId);
            } catch (_) {
              /* ignore */
            }
          });

          el.addEventListener('pointermove', (e) => {
            if (!drag || e.pointerId !== drag.pointerId) return;
            if (e.buttons !== 1) return;
            const dx = e.clientX - drag.startX;
            if (Math.abs(dx) > 5) drag.moved = true;
            el.scrollLeft = drag.startScroll - dx;
            if (drag.moved) {
              el.classList.add('card-inline-variants__hover-options--dragging');
            }
          });

          const endDrag = (e) => {
            if (!drag || e.pointerId !== drag.pointerId) return;
            try {
              el.releasePointerCapture(e.pointerId);
            } catch (_) {
              /* ignore */
            }
            if (drag.moved) {
              el._suppressNextClick = true;
            }
            drag = null;
            el.classList.remove('card-inline-variants__hover-options--dragging');
          };

          el.addEventListener('pointerup', endDrag);
          el.addEventListener('pointercancel', endDrag);

          el.addEventListener(
            'click',
            (e) => {
              if (el._suppressNextClick) {
                e.preventDefault();
                e.stopPropagation();
                el._suppressNextClick = false;
              }
            },
            true
          );
        });
      }
      
      onHoverOptionClick(event) {
        const input = event.target;
        const optionIndex = parseInt(input.dataset.optionIndex, 10);
        const optionValue = input.dataset.optionValue;
        
        // Update selected options (even if already selected)
        this.selectedOptions[optionIndex] = optionValue;
        
        // Ensure variant is updated
        this.updateSelectedVariant();
        this.updateAvailability();
        
        // Do not add here - same click triggers both 'click' and 'change'; add only in onOptionChange
      }

      onMoreClick(event) {
        const btn = event.target;
        const optionValues = btn.closest('.card-inline-variants__option-values');
        if (optionValues) {
          optionValues.classList.add('card-inline-variants__option-values--expanded');
        }
      }

      onOptionChange(event) {
        const input = event.target;
        const optionIndex = parseInt(input.dataset.optionIndex, 10);
        const optionValue = input.dataset.optionValue;
        const isHoverInput = input.classList.contains('card-inline-variants__input--hover');

        // Update selected options
        this.selectedOptions[optionIndex] = optionValue;

        // Find and update the selected variant
        this.updateSelectedVariant();
        
        // Update the product image
        this.updateProductImage();
        
        // Update variant availability indicators
        this.updateAvailability();
        
        // Update the selected variant name display
        this.updateSelectedName();
        
        // If hover option (non-color) or button mode: add to cart when variant is complete.
        // product-form submit is no-op for card-inline-variants to avoid double-add.
        if (isHoverInput && this.currentVariant && this.currentVariant.available) {
          this.addToCartDirectly();
        } else if (this.buttonMode && this.currentVariant && this.currentVariant.available) {
          this.addToCartDirectly();
        }
      }

      addToCartDirectly() {
        if (!this.currentVariant) return;
        
        const formData = {
          items: [{
            id: this.currentVariant.id,
            quantity: 1
          }]
        };

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formData)
        })
        .then(response => response.json())
        .then(data => {
          // Trigger cart update
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'card-inline-variants' });
          }
          
          // Try to open Rebuy cart
          this.openCart();
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
        });
      }

      openCart() {
        // Check for Rebuy
        if (window.Rebuy) {
          if (window.Rebuy.Cart && typeof window.Rebuy.Cart.show === 'function') {
            window.Rebuy.Cart.show();
            return;
          }
          if (window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
            window.Rebuy.SmartCart.show();
            return;
          }
        }
        
        // Fallback: try to click the cart icon
        const cartIcon = document.querySelector('a[href="/cart"], .cart-icon-bubble, [data-cart-toggle]');
        if (cartIcon) {
          cartIcon.click();
        }
      }

      updateSelectedVariant() {
        if (!this.productData) return;

        const variantInput = this.querySelector('input[name="id"]');
        const addButton = this.querySelector('.card-inline-variants__add-button');
        const addText = this.querySelector('.card-inline-variants__add-text');
        const soldOutText = this.querySelector('.card-inline-variants__sold-out-text');

        // Check if all options have been selected
        const totalOptions = this.productData.options.length;
        const selectedCount = this.selectedOptions.filter(opt => opt !== undefined && opt !== null).length;
        
        if (selectedCount < totalOptions) {
          // Not all options selected yet
          this.currentVariant = null;
          addButton.disabled = true;
          addText.textContent = 'Select an option';
          if (soldOutText) soldOutText.classList.add('hidden');
          this.updateProductLinks();
          return;
        }

        // Find the variant that matches all selected options
        const matchingVariant = this.productData.variants.find((variant) => {
          return this.selectedOptions.every((option, index) => {
            const optionKey = `option${index + 1}`;
            return variant[optionKey] === option;
          });
        });

        this.currentVariant = matchingVariant;

        if (matchingVariant) {
          variantInput.value = matchingVariant.id;
          
          // Get the option type name (e.g., "Color", "Size")
          const optionType = this.productData.options[0] || '';
          
          // Format price
          const price = matchingVariant.price / 100;
          const formattedPrice = '$' + price.toFixed(2);
          
          if (matchingVariant.available) {
            addButton.disabled = false;
            addText.innerHTML = `+ Quick Add ${optionType} <span class="card-inline-variants__price">(${formattedPrice})</span>`;
            addText.classList.remove('hidden');
            if (soldOutText) soldOutText.classList.add('hidden');
          } else {
            addButton.disabled = true;
            addText.textContent = window.variantStrings?.soldOut || 'Sold out';
            addText.classList.remove('hidden');
            if (soldOutText) soldOutText.classList.add('hidden');
          }
        } else {
          // No matching variant - likely an unavailable combination
          addButton.disabled = true;
          addText.textContent = window.variantStrings?.unavailable || 'Unavailable';
        }

        this.updateProductLinks();
      }

      updateProductLinks() {
        const productUrl = this.dataset.productUrl;
        if (!productUrl) return;

        const cardWrapper = this.closest('.card-wrapper, featured-collection-product');
        if (!cardWrapper) return;

        const baseUrl = productUrl.split('?')[0];
        const newHref = this.currentVariant
          ? `${baseUrl}?variant=${this.currentVariant.id}`
          : baseUrl;

        const links = cardWrapper.querySelectorAll('a[href]');
        links.forEach((link) => {
          const href = link.getAttribute('href');
          if (!href) return;
          const linkPath = href.split('?')[0];
          if (linkPath === baseUrl) {
            link.setAttribute('href', newHref);
          }
        });
      }

      updateProductImage() {
        if (!this.currentVariant || !this.currentVariant.featured_media) return;

        // Find the product card wrapper
        const cardWrapper = this.closest('.card-wrapper, featured-collection-product');
        if (!cardWrapper) return;

        // Find the product image
        const productImage = cardWrapper.querySelector('.product-card-image, .card__media img:first-child');
        if (!productImage) return;

        const newImageUrl = this.currentVariant.featured_media.url;
        if (!newImageUrl) return;

        // Generate responsive srcset
        const baseUrl = newImageUrl.split('?')[0];
        const sizes = [165, 360, 533, 720, 940, 1066];
        const srcsetParts = sizes.map(size => `${baseUrl}?width=${size} ${size}w`);

        // Update image
        productImage.srcset = srcsetParts.join(', ');
        productImage.src = newImageUrl;

        // Hide secondary image if present
        const mediaContainer = cardWrapper.querySelector('.media--hover-effect');
        if (mediaContainer) {
          const secondaryImage = mediaContainer.querySelector('img:nth-child(2)');
          if (secondaryImage) {
            secondaryImage.style.display = 'none';
            secondaryImage.style.opacity = '0';
          }
          mediaContainer.classList.add('variant-selected');
        }

        // Add variant-selected class to card wrapper
        cardWrapper.classList.add('variant-selected');
      }

      updateSelectedName() {
        // Find the selected name display element
        const cardWrapper = this.closest('.card-wrapper, featured-collection-product');
        if (!cardWrapper) return;
        
        const selectedNameEl = cardWrapper.querySelector('.card-inline-variants__selected-name');
        if (!selectedNameEl) return;
        
        // Get the first selected option value (usually Color)
        const selectedValue = this.selectedOptions[0];
        
        if (selectedValue) {
          selectedNameEl.textContent = selectedValue;
        } else {
          selectedNameEl.textContent = '';
        }
      }

      updateAvailability() {
        if (!this.productData) return;

        // For each option position, determine which values have available variants
        // given the current selections for other options
        const optionFieldsets = this.querySelectorAll('.card-inline-variants__option');
        
        optionFieldsets.forEach((fieldset) => {
          // Get the actual option index from the data attribute
          const currentOptionIndex = parseInt(fieldset.dataset.optionIndex, 10);
          const inputs = fieldset.querySelectorAll('.card-inline-variants__input');
          
          inputs.forEach((input) => {
            const optionValue = input.dataset.optionValue;
            const label = input.nextElementSibling;
            
            const isAvailable = this.checkOptionAvailability(currentOptionIndex, optionValue);

            // Update availability indicator
            if (isAvailable) {
              input.removeAttribute('data-unavailable');
              label.classList.remove('card-inline-variants__label--unavailable');
            } else {
              input.setAttribute('data-unavailable', 'true');
              label.classList.add('card-inline-variants__label--unavailable');
            }
          });
        });
        
        // Also update hover options (size buttons) availability
        const hoverInputs = this.querySelectorAll('.card-inline-variants__input--hover');
        hoverInputs.forEach((input) => {
          const optionIndex = parseInt(input.dataset.optionIndex, 10);
          const optionValue = input.dataset.optionValue;
          const label = input.nextElementSibling;
          
          const isAvailable = this.checkOptionAvailability(optionIndex, optionValue);
          
          if (isAvailable) {
            input.removeAttribute('data-unavailable');
            label.classList.remove('card-inline-variants__hover-label--unavailable');
          } else {
            input.setAttribute('data-unavailable', 'true');
            label.classList.add('card-inline-variants__hover-label--unavailable');
          }
        });
      }
      
      checkOptionAvailability(currentOptionIndex, optionValue) {
        // Check if any variant with this option value is available
        // considering other selected options (only if they are selected)
        return this.productData.variants.some((variant) => {
          // Check if this variant has the option value we're testing
          const optionKey = `option${currentOptionIndex + 1}`;
          if (variant[optionKey] !== optionValue) return false;
          
          // Check if variant matches other selected options (only check selected ones)
          for (let i = 0; i < this.productData.options.length; i++) {
            if (i === currentOptionIndex) continue;
            // Only check if this option has been selected
            if (this.selectedOptions[i] !== undefined && this.selectedOptions[i] !== null) {
              const otherOptionKey = `option${i + 1}`;
              if (variant[otherOptionKey] !== this.selectedOptions[i]) return false;
            }
          }
          
          return variant.available;
        });
      }
    }
  );
}

// Apply custom color swatches if available
document.addEventListener('DOMContentLoaded', () => {
  applyCardInlineSwatchColors();
});

function applyCardInlineSwatchColors() {
  // Check if window.swatchColors exists (from swatch color JSON)
  if (!window.swatchColors) return;

  const swatchLabels = document.querySelectorAll('.card-inline-variants__label--swatch[data-swatch-color]');
  
  swatchLabels.forEach((label) => {
    const colorName = label.dataset.swatchColor;
    const swatchData = window.swatchColors[colorName] || window.swatchColors[colorName.toLowerCase()];
    
    if (swatchData) {
      if (swatchData.image) {
        label.style.setProperty('--swatch-background', `url(${swatchData.image})`);
      } else if (swatchData.color) {
        label.style.setProperty('--swatch-background', swatchData.color);
      }
    }
  });
}
